/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-invitations
 * @responsibility Own the backend-authoritative SET-F06 organization invitation queries and their
 *   create/resend/revoke commands from the Settings Invitations tab.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; active-role provider; same-module API and contracts;
 *   TanStack Query
 * @forbidden component UI; fabricated organization data outside the environment.demoMode branch;
 *   direct Supabase calls; email/provider adapters
 * @public-api settings-invitations hooks
 * @side-effects authenticated invitation reads/writes and cache invalidation
 * @tests frontend/packages/workspace-web/src/modules/settings-invitations/api/settings-invitations.queries.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import type {
  OrganizationInvitation,
  OrganizationInvitationCreateInput,
  OrganizationInvitationListResponse,
  OrganizationInvitationRole,
} from '@vispo/contracts';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import {
  createOrganizationInvitation,
  listOrganizationInvitations,
  resendOrganizationInvitation,
  revokeOrganizationInvitation,
} from './settings-invitations';

const DEMO_ORGANIZATION_ID = 'demo-organization';

function seedDemoInvitations(): OrganizationInvitation[] {
  return [
    {
      id: 'agent-a-set-f06-demo-invitation',
      recipientEmail: 'demo-invitee@example.invalid',
      invitedRole: 'staff',
      status: 'pending',
      revision: 1,
      expiresAt: '2026-12-31T00:00:00Z',
    },
  ] as unknown as OrganizationInvitation[];
}

function demoStoreKey(organizationId: string) {
  return ['set-f06', 'invitations-demo-store', organizationId] as const;
}

/** Demo mode has no backend, so create/resend/revoke must persist into the SAME cache the list
 * query reads — otherwise every mutation would appear to work but vanish on the next refetch. */
function readDemoStore(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
): OrganizationInvitation[] {
  const key = demoStoreKey(organizationId);
  const existing = queryClient.getQueryData<OrganizationInvitation[]>(key);
  if (existing) return existing;
  const seeded = seedDemoInvitations();
  queryClient.setQueryData(key, seeded);
  return seeded;
}

function writeDemoStore(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
  invitations: readonly OrganizationInvitation[],
): void {
  queryClient.setQueryData(demoStoreKey(organizationId), invitations);
}

export const settingsInvitationsKeys = {
  invitations: (userId: string, organizationId: string, status: string) =>
    ['set-f06', 'invitations', userId, organizationId, status] as const,
};

function effectiveOrganizationId(
  activeOrganizationId: string | null,
  demoMode: boolean | undefined,
): string | null {
  return activeOrganizationId ?? (demoMode ? DEMO_ORGANIZATION_ID : null);
}

function forcedNonAdmin(): boolean {
  return Boolean(
    (globalThis as typeof globalThis & { __VISPO_SET_F06_FORCE_NON_ADMIN__?: boolean })
      .__VISPO_SET_F06_FORCE_NON_ADMIN__,
  );
}

function forcedRevisionConflict(): boolean {
  return Boolean(
    (globalThis as typeof globalThis & { __VISPO_SET_F06_FORCE_REVISION_CONFLICT__?: boolean })
      .__VISPO_SET_F06_FORCE_REVISION_CONFLICT__,
  );
}

export interface OrganizationInvitationsAccess {
  readonly organizationId: string | null;
  readonly canManage: boolean;
}

/** Mirrors SET-F05: list_organization_invitations is itself owner/admin-gated, so a non-admin
 * gets the same blocked state as the roster read, never a read-only view. */
export function useOrganizationInvitationsAccess() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);

  return useQuery({
    queryKey: [
      'set-f06',
      'invitations-access',
      session?.userId ?? 'anonymous',
      organizationId ?? 'none',
    ],
    enabled: Boolean(environment && (session || environment.demoMode)),
    queryFn: async (): Promise<OrganizationInvitationsAccess> => {
      if (environment?.demoMode) {
        return { organizationId, canManage: !forcedNonAdmin() };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      // Reuses the same me-context membership signal SET-F05 already resolves; invitations are
      // only ever owner/admin-manageable, matching create/resend/revoke's own backend gate.
      const { loadMeContext } = await import('../../auth');
      const context = await loadMeContext(environment.apiClient);
      const organization = context.organizations.find(
        (candidate) => candidate.id === organizationId,
      );
      return {
        organizationId: organization?.id ?? null,
        canManage: Boolean(
          organization && ['owner', 'admin'].includes(organization.membership_role),
        ),
      };
    },
  });
}

export function useOrganizationInvitations(
  organizationId: string | null,
  status: string,
  enabled: boolean,
) {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: settingsInvitationsKeys.invitations(userId, organizationId ?? 'none', status),
    enabled: Boolean(environment && organizationId && enabled),
    queryFn: async (): Promise<OrganizationInvitationListResponse> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (environment?.demoMode) {
        const all = readDemoStore(queryClient, organizationId);
        const invitations = status === 'all' ? all : all.filter((item) => item.status === status);
        return { organizationId, invitations: invitations.map((item) => ({ ...item })) };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      return listOrganizationInvitations(environment.apiClient, organizationId, status);
    },
    retry: 1,
  });
}

function useInvitationCommandContext(status: string) {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const activeOrganizationId = useActiveOrganizationId();
  const organizationId = effectiveOrganizationId(activeOrganizationId, environment?.demoMode);
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = settingsInvitationsKeys.invitations(userId, organizationId ?? 'none', status);
  return { environment, organizationId, queryClient, queryKey };
}

function invalidateInvitationQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  organizationId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ['set-f06', 'invitations', userId, organizationId],
  });
}

export function useCreateOrganizationInvitation(status: string) {
  const { environment, organizationId, queryClient, queryKey } =
    useInvitationCommandContext(status);
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  return useMutation({
    mutationFn: async (
      input: Pick<
        OrganizationInvitationCreateInput,
        'expiresAt' | 'invitedRole' | 'recipientEmail'
      >,
    ): Promise<OrganizationInvitation> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (environment?.demoMode) {
        const current = readDemoStore(queryClient, organizationId);
        if (
          current.some(
            (item) => item.recipientEmail === input.recipientEmail && item.status === 'pending',
          )
        ) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_INVITATION_ACTIVE',
              message: '此使用者已經有一筆待處理的邀請。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const created: OrganizationInvitation = {
          id: `demo-invitation-${crypto.randomUUID()}`,
          recipientEmail: input.recipientEmail,
          invitedRole: input.invitedRole,
          status: 'pending',
          revision: 1,
          expiresAt: input.expiresAt,
          token: `demo-token-${crypto.randomUUID()}`,
        } as unknown as OrganizationInvitation;
        writeDemoStore(queryClient, organizationId, [...current, created]);
        return created;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await createOrganizationInvitation(environment.apiClient, organizationId, {
        ...input,
        idempotencyKey: `agent-a-set-f06-${crypto.randomUUID()}`,
      });
      return response.invitation;
    },
    onSuccess: () => {
      if (organizationId) invalidateInvitationQueries(queryClient, userId, organizationId);
      else void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useResendOrganizationInvitation(status: string) {
  const { environment, organizationId, queryClient, queryKey } =
    useInvitationCommandContext(status);
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  return useMutation({
    mutationFn: async (input: {
      expectedRevision: number;
      invitationId: string;
    }): Promise<OrganizationInvitation> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (environment?.demoMode) {
        if (forcedRevisionConflict()) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_INVITATION_STATE_CONFLICT',
              message: '資料已更新，請重新整理後再試。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const current = readDemoStore(queryClient, organizationId);
        const target = current.find((item) => item.id === input.invitationId);
        if (!target) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_INVITATION_NOT_FOUND',
              message: '找不到這筆邀請。',
              details: undefined,
              requestId: 'demo',
            },
            404,
          );
        }
        const rotated: OrganizationInvitation = {
          ...target,
          revision: target.revision + 1,
          token: `demo-token-${crypto.randomUUID()}`,
        };
        writeDemoStore(
          queryClient,
          organizationId,
          current.map((item) => (item.id === rotated.id ? rotated : item)),
        );
        return rotated;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await resendOrganizationInvitation(
        environment.apiClient,
        organizationId,
        input.invitationId,
        {
          expectedRevision: input.expectedRevision,
          idempotencyKey: `agent-a-set-f06-${crypto.randomUUID()}`,
        },
      );
      return response.invitation;
    },
    onSuccess: () => {
      if (organizationId) invalidateInvitationQueries(queryClient, userId, organizationId);
      else void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useRevokeOrganizationInvitation(status: string) {
  const { environment, organizationId, queryClient, queryKey } =
    useInvitationCommandContext(status);
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  return useMutation({
    mutationFn: async (input: {
      expectedRevision: number;
      invitationId: string;
    }): Promise<OrganizationInvitation> => {
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      if (environment?.demoMode) {
        if (forcedRevisionConflict()) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_INVITATION_STATE_CONFLICT',
              message: '資料已更新，請重新整理後再試。',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        const current = readDemoStore(queryClient, organizationId);
        const target = current.find((item) => item.id === input.invitationId);
        if (!target) {
          throw new VispoApiError(
            {
              code: 'ORGANIZATION_INVITATION_NOT_FOUND',
              message: '找不到這筆邀請。',
              details: undefined,
              requestId: 'demo',
            },
            404,
          );
        }
        const revoked: OrganizationInvitation = {
          ...target,
          status: 'revoked',
          revision: target.revision + 1,
          token: undefined,
        };
        writeDemoStore(
          queryClient,
          organizationId,
          current.map((item) => (item.id === revoked.id ? revoked : item)),
        );
        return revoked;
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await revokeOrganizationInvitation(
        environment.apiClient,
        organizationId,
        input.invitationId,
        {
          expectedRevision: input.expectedRevision,
          idempotencyKey: `agent-a-set-f06-${crypto.randomUUID()}`,
        },
      );
      return response.invitation;
    },
    onSuccess: () => {
      if (organizationId) invalidateInvitationQueries(queryClient, userId, organizationId);
      else void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export type { OrganizationInvitationRole };
